// Circuit breaker for OTLP exporters.
//
// Why this exists (2026-08-14 trace + HAR audit of atriam.ai/finances):
// `web.ts` ran the browser trace exporter with `scheduledDelayMillis` EQUAL to
// `exportTimeoutMillis` (both 10s). On a degraded connection every scheduled
// export hung the full 10s and the next batch went out the instant it gave up
// — a 100% duty cycle on one of the browser's per-origin connection slots, for
// the entire session, competing with user-facing fetches that were already
// starved. The HAR showed 31 `/api/otlp/traces` POSTs, 12 aborted at exactly
// 10.00s, on exact 10-second centers, while page data sat queued in `blocked`.
//
// Two independent guards fix that, and both are needed:
//   1. export timeout strictly below the schedule interval (see web.ts) so a
//      hung export always releases its slot before the next one starts;
//   2. this breaker, so a sustained outage stops dialing out entirely instead
//      of drip-feeding a doomed request forever.
//
// Telemetry must never outbid the application for the network.

import { ExportResultCode } from "@opentelemetry/core";
import type { ExportResult } from "@opentelemetry/core";

/**
 * The structural shape both `SpanExporter` (sdk-trace-base) and
 * `LogRecordExporter` (sdk-logs) satisfy. Typing against the shape rather than
 * either concrete interface lets one breaker wrap both.
 */
export interface MinimalExporter<T> {
  export(items: T[], resultCallback: (result: ExportResult) => void): void;
  shutdown(): Promise<void>;
  forceFlush?(): Promise<void>;
}

/**
 * What the breaker hands back. `forceFlush` is REQUIRED here even though it is
 * optional on the input: `LogRecordExporter` (sdk-logs) declares it required
 * while `SpanExporter` (sdk-trace-base) declares it optional, so an optional
 * member on the return type is not assignable to the log processor. The wrapper
 * always supplies one, so stating that in the type costs nothing.
 */
export interface WrappedExporter<T> {
  export(items: T[], resultCallback: (result: ExportResult) => void): void;
  shutdown(): Promise<void>;
  forceFlush(): Promise<void>;
}

export interface CircuitBreakerOptions {
  /** Consecutive failed exports that trip the breaker open. */
  trippingFailures?: number;
  /** How long to stay open before letting a single probe batch through. */
  cooldownMillis?: number;
  /** Injectable clock — tests drive this instead of waiting on wall time. */
  now?: () => number;
}

/**
 * Wrap an OTLP exporter so a sustained export outage stops producing network
 * requests. After `trippingFailures` consecutive failures the breaker opens and
 * every batch is dropped locally (reported FAILED, no fetch) until
 * `cooldownMillis` elapses. The first export after the cooldown is a probe: it
 * goes out for real, and its result either closes the breaker or re-opens it
 * for another cooldown.
 *
 * Dropping telemetry under a persistent outage is the intended trade. The spans
 * were already being dropped — by the export timeout — just at the cost of a
 * connection slot each time.
 */
export function withCircuitBreaker<T>(
  exporter: MinimalExporter<T>,
  options: CircuitBreakerOptions = {},
): WrappedExporter<T> {
  const trippingFailures = options.trippingFailures ?? 3;
  const cooldownMillis = options.cooldownMillis ?? 60_000;
  const now = options.now ?? (() => Date.now());

  let consecutiveFailures = 0;
  let openUntil = 0;

  return {
    export(items, resultCallback) {
      if (now() < openUntil) {
        // Breaker open: fail locally. No fetch, no connection slot consumed.
        resultCallback({
          code: ExportResultCode.FAILED,
          error: new Error("otlp_exporter_circuit_open"),
        });
        return;
      }

      exporter.export(items, (result) => {
        if (result.code === ExportResultCode.SUCCESS) {
          consecutiveFailures = 0;
          openUntil = 0;
        } else {
          consecutiveFailures += 1;
          if (consecutiveFailures >= trippingFailures) {
            openUntil = now() + cooldownMillis;
            // Re-arm at the threshold so a failing probe re-opens immediately
            // rather than needing another full run of failures to trip again.
            consecutiveFailures = trippingFailures;
          }
        }
        resultCallback(result);
      });
    },
    shutdown() {
      return exporter.shutdown();
    },
    forceFlush() {
      return exporter.forceFlush?.() ?? Promise.resolve();
    },
  };
}
